import { HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, Optional, PLATFORM_ID, Provider, TransferState } from '@angular/core';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { MessageService } from 'primeng/api';

import { Environment } from '@environment';
import { AuthService } from '@usheru-hff/authentication/domain';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { CountryService } from '@usheru-hff/sdk/country';
import { LocationService, VIEWER_COUNTRY_KEY } from '@usheru-hff/sdk/location';
import { CacheInterceptor } from '@usheru-hff/shared/cache-interceptor';
import { COUNTRY_VERSION_KEY, COUNTRY_VERSION_TOKEN, VIEWER_COUNTRY_TOKEN } from '@usheru-hff/shared/tokens';
import { TranslationsService } from '@usheru-hff/shared/utils-translation';

export function provideBaseServices(environment: Environment, args?: { skipLocationService?: boolean }): Provider {
  // CollectionsService and ChannelService
  return {
    provide: APP_INITIALIZER,
    useFactory: (
      channelService: ChannelService,
      locationService: LocationService,
      translationService: TranslationsService
    ) => {
      const { skipLocationService } = args || {};

      return () =>
        Promise.all([
          channelService.init().then(() => translationService.init()),
          !skipLocationService ? locationService.init() : null
        ]);
    },
    deps: [ChannelService, LocationService, TranslationsService],
    multi: true
  };
}
// export function provideSocialLoginServices(environment: Environment): Provider {
//   const allowLogin = environment.customPageParams?.login || true;
//   const providers = [];

//   return {
//     provide: 'SocialAuthServiceConfig',
//     useValue: {
//       autoLogin: false,
//       providers: null
//     } as any
//   };
// }

export function provideCacheInterceptor(): Provider {
  return {
    provide: HTTP_INTERCEPTORS,
    useClass: CacheInterceptor,
    multi: true
  };
}

export function provideAuthResource(environment: Environment): Provider {
  return [
    MessageService,
    {
      provide: AuthService,
      useFactory: (
        cookieService: CookieService,
        httpClient: HttpClient,
        router: Router,
        countryService: CountryService,
        messageService: MessageService
      ) => new AuthService(environment, cookieService, router, httpClient, countryService, messageService),
      deps: [CookieService, HttpClient, Router, CountryService, MessageService]
    }
  ];
}

export function provideCountryVersionStateSync(): Provider {
  return {
    provide: APP_INITIALIZER,
    multi: true,
    useFactory:
      (ts: TransferState, platformId: any, countryVersionToken?: string, viewerCountryToken?: string) => () => {
        ts.set(COUNTRY_VERSION_KEY, countryVersionToken ?? null);
        ts.set(VIEWER_COUNTRY_KEY, viewerCountryToken ?? null);
      },
    deps: [TransferState, PLATFORM_ID, [new Optional(), COUNTRY_VERSION_TOKEN], [new Optional(), VIEWER_COUNTRY_TOKEN]]
  };
}

export function provideCountryVersionFromTransferState(): Provider {
  return [
    {
      provide: COUNTRY_VERSION_TOKEN,
      useFactory: (ts: TransferState, platformId: any) => ts.get<string | null>(COUNTRY_VERSION_KEY, null),
      deps: [TransferState, PLATFORM_ID]
    },
    {
      provide: VIEWER_COUNTRY_TOKEN,
      useFactory: (ts: TransferState, platformId: any) => ts.get<string | null>(VIEWER_COUNTRY_KEY, null),
      deps: [TransferState, PLATFORM_ID]
    }
  ];
}
